import { AlertTriangle, RotateCcw } from "lucide-react";
import { useState, type FC } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";

import { legacyGenerateDraft } from "../../../api/drafts-v2";
import type { Draft } from "../../../api/types/draft";
import { retryText } from "../chat/components/RunFailureCard";
import {
  draftStatus,
  draftTitle,
  friendlyRunFailure
} from "../workspace/workspaceModel";
import { isClassicDraft } from "./classicModel";

/** The latest run's failure on a draft that has nothing written, or null. */
export const emptyDraftFailure = (
  draft: Draft | undefined
): { title: string; message: string | null; retryable: boolean } | null => {
  if (!draft || draft.docx_storage_path || draftStatus(draft) !== "failed")
    return null;
  const failure = draft.display_failure ?? draft.agent_failure ?? null;
  const message =
    failure?.message?.trim() || draft.agent_failure_reason?.trim() || null;
  return {
    title: friendlyRunFailure(message, failure?.code),
    message,
    retryable: failure?.retryable !== false
  };
};

type Props = {
  draft: Draft;
  /** Puts the retry request in the chat composer. */
  onRetry: (text: string) => void;
  /** After Retry Classic queued a new run, so the pane refetches the draft. */
  onClassicRetried?: () => void;
};

/**
 * Where the editor would be for a draft whose only run failed: why it failed
 * and Retry, rather than an empty document that reads as never started.
 */
export const DraftFailedState: FC<Props> = ({
  draft,
  onRetry,
  onClassicRetried
}) => {
  const [retrying, setRetrying] = useState(false);
  const failure = emptyDraftFailure(draft);
  if (!failure) return null;
  const caseyRetry = () =>
    onRetry(
      retryText({
        title: draftTitle(draft),
        research: false,
        instructions: null
      })
    );

  if (isClassicDraft(draft)) {
    const retryClassic = async () => {
      setRetrying(true);
      try {
        await legacyGenerateDraft(draft.draft_id, {
          templateId: draft.classic_template_id ?? null,
          letterheadId: draft.classic_letterhead_id ?? null,
          omitResearch: draft.classic_omitted_research ?? []
        });
        onClassicRetried?.();
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : "Couldn't start the classic draft."
        );
      } finally {
        setRetrying(false);
      }
    };
    return (
      <EmptyState
        icon={AlertTriangle}
        title="The Classic Draft Couldn't Be Written"
        className="h-full"
        data-testid="draft-failed"
        description={
          failure.message ??
          "Your answers and finished research are kept, so trying again only redoes what's missing."
        }
        action={
          <>
            <Button
              variant="primary"
              size="sm"
              disabled={retrying}
              onClick={() => void retryClassic()}
              data-testid="draft-failed-retry-classic"
            >
              <RotateCcw className="size-3.5" /> Retry Classic
            </Button>
            <Button variant="secondary" size="sm" onClick={caseyRetry}>
              Draft With Casey Instead
            </Button>
          </>
        }
      />
    );
  }

  return (
    <EmptyState
      icon={AlertTriangle}
      title={failure.title}
      className="h-full"
      data-testid="draft-failed"
      description={
        failure.retryable
          ? "Nothing was written. Retry asks the agent to draft it again."
          : failure.message
      }
      action={
        failure.retryable ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={caseyRetry}
            data-testid="draft-failed-retry"
          >
            <RotateCcw className="size-3.5" /> Retry in Chat
          </Button>
        ) : null
      }
    />
  );
};
